import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TimelineEditorProps,
  TimelineStep,
  StepBoundary,
} from "../../types/timeline";
import { TimelineRuler } from "./timeline/TimelineRuler";
import { TimelineTrack } from "./timeline/TimelineTrack";
import { StepInspector } from "./timeline/StepInspector";
import {
  getTotalFrames,
  secondsToFrames,
  frameToTimeLabel,
} from "../../utils/timelineMath";
import {
  sanitizeStep,
  updateStepBoundary,
} from "../../utils/timelineValidation";
import { ViewerSettings } from "../../types/settings";

const STEP_COLOR_PALETTE = [
  "#38bdf8",
  "#22c55e",
  "#f97316",
  "#a78bfa",
  "#eab308",
];

const MONOSPACE_FONT =
  "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace";

type LanguageCode = ViewerSettings["language"];

function getTimelineCopy(language: LanguageCode) {
  switch (language) {
    case "es":
      return {
        stepLabel: "Paso",
        pausePlayback: "Pausar reproducci\u00f3n de la l\u00ednea de tiempo",
        playPlayback: "Reproducir l\u00ednea de tiempo",
        playing: "Reproduciendo",
        paused: "Pausado",
        currentFrame: "Fotograma actual",
        totalFrames: "Fotogramas totales",
        currentTime: "Tiempo actual",
        saved: "Guardado",
        selectStep:
          "Selecciona un paso para editar su nombre y rango de fotogramas.",
        stepName: "Nombre del paso",
        stepText: "Texto del paso",
        stepTextPlaceholder:
          "Describe lo que el usuario debe hacer en este paso...",
        startFrame: "Fotograma inicial",
        endFrame: "Fotograma final",
        saveStep: "Guardar paso",
        deleteStep: "Eliminar paso",
        untitledStep: "Paso sin t\u00edtulo",
        id: "ID",
        startHandle: "Control inicial",
        endHandle: "Control final",
        handleFor: "para",
        playhead: "Cabezal del fotograma actual",
        seekAnimation: "Arrastra el cabezal para buscar en la animaci\u00f3n",
        stepRange: "Rango del paso",
      };
    case "fr":
      return {
        stepLabel: "\u00c9tape",
        pausePlayback: "Mettre la lecture de la chronologie en pause",
        playPlayback: "Lire la chronologie",
        playing: "Lecture en cours",
        paused: "En pause",
        currentFrame: "Image actuelle",
        totalFrames: "Nombre total d\u2019images",
        currentTime: "Temps actuel",
        saved: "Enregistr\u00e9",
        selectStep:
          "S\u00e9lectionnez une \u00e9tape pour modifier son nom et sa plage d\u2019images.",
        stepName: "Nom de l\u2019\u00e9tape",
        stepText: "Texte de l\u2019\u00e9tape",
        stepTextPlaceholder:
          "D\u00e9crivez ce que l\u2019utilisateur doit faire \u00e0 cette \u00e9tape...",
        startFrame: "Image de d\u00e9but",
        endFrame: "Image de fin",
        saveStep: "Enregistrer l\u2019\u00e9tape",
        deleteStep: "Supprimer l\u2019\u00e9tape",
        untitledStep: "\u00c9tape sans titre",
        id: "ID",
        startHandle: "Poign\u00e9e de d\u00e9but",
        endHandle: "Poign\u00e9e de fin",
        handleFor: "pour",
        playhead: "T\u00eate de lecture de l\u2019image actuelle",
        seekAnimation:
          "Faites glisser la t\u00eate de lecture pour naviguer dans l\u2019animation",
        stepRange: "Plage de l\u2019\u00e9tape",
      };
    case "de":
      return {
        stepLabel: "Schritt",
        pausePlayback: "Timeline-Wiedergabe anhalten",
        playPlayback: "Timeline abspielen",
        playing: "Wird abgespielt",
        paused: "Pausiert",
        currentFrame: "Aktueller Frame",
        totalFrames: "Gesamtzahl Frames",
        currentTime: "Aktuelle Zeit",
        saved: "Gespeichert",
        selectStep:
          "W\u00e4hlen Sie einen Schritt aus, um Namen und Frame-Bereich zu bearbeiten.",
        stepName: "Schrittname",
        stepText: "Schritttext",
        stepTextPlaceholder:
          "Beschreiben Sie, was der Benutzer in diesem Schritt tun soll...",
        startFrame: "Start-Frame",
        endFrame: "End-Frame",
        saveStep: "Schritt speichern",
        deleteStep: "Schritt l\u00f6schen",
        untitledStep: "Unbenannter Schritt",
        id: "ID",
        startHandle: "Startgriff",
        endHandle: "Endgriff",
        handleFor: "f\u00fcr",
        playhead: "Abspielkopf des aktuellen Frames",
        seekAnimation: "Abspielkopf ziehen, um in der Animation zu suchen",
        stepRange: "Schrittbereich",
      };
    case "zh":
      return {
        stepLabel: "\u6b65\u9aa4",
        pausePlayback: "\u6682\u505c\u65f6\u95f4\u7ebf\u64ad\u653e",
        playPlayback: "\u64ad\u653e\u65f6\u95f4\u7ebf",
        playing: "\u64ad\u653e\u4e2d",
        paused: "\u5df2\u6682\u505c",
        currentFrame: "\u5f53\u524d\u5e27",
        totalFrames: "\u603b\u5e27\u6570",
        currentTime: "\u5f53\u524d\u65f6\u95f4",
        saved: "\u5df2\u4fdd\u5b58",
        selectStep:
          "\u9009\u62e9\u4e00\u4e2a\u6b65\u9aa4\u4ee5\u7f16\u8f91\u5176\u540d\u79f0\u548c\u5e27\u8303\u56f4\u3002",
        stepName: "\u6b65\u9aa4\u540d\u79f0",
        stepText: "\u6b65\u9aa4\u6587\u672c",
        stepTextPlaceholder:
          "\u63cf\u8ff0\u7528\u6237\u5728\u6b64\u6b65\u9aa4\u4e2d\u5e94\u6267\u884c\u7684\u64cd\u4f5c...",
        startFrame: "\u5f00\u59cb\u5e27",
        endFrame: "\u7ed3\u675f\u5e27",
        saveStep: "\u4fdd\u5b58\u6b65\u9aa4",
        deleteStep: "\u5220\u9664\u6b65\u9aa4",
        untitledStep: "\u672a\u547d\u540d\u6b65\u9aa4",
        id: "\u7f16\u53f7",
        startHandle: "\u5f00\u59cb\u63a7\u4ef6",
        endHandle: "\u7ed3\u675f\u63a7\u4ef6",
        handleFor: "\u7528\u4e8e",
        playhead: "\u5f53\u524d\u5e27\u64ad\u5934",
        seekAnimation: "\u62d6\u52a8\u64ad\u5934\u4ee5\u67e5\u627e\u52a8\u753b",
        stepRange: "\u6b65\u9aa4\u8303\u56f4",
      };
    case "en":
    default:
      return {
        stepLabel: "Step",
        pausePlayback: "Pause timeline playback",
        playPlayback: "Play timeline playback",
        playing: "Playing",
        paused: "Paused",
        currentFrame: "Current Frame",
        totalFrames: "Total Frames",
        currentTime: "Current Time",
        saved: "Saved",
        selectStep: "Select a step to edit its name and frame range.",
        stepName: "Step Name",
        stepText: "Step Text",
        stepTextPlaceholder: "Describe what the user should do in this step...",
        startFrame: "Start Frame",
        endFrame: "End Frame",
        saveStep: "Save Step",
        deleteStep: "Delete Step",
        untitledStep: "Untitled Step",
        id: "ID",
        startHandle: "Start handle",
        endHandle: "End handle",
        handleFor: "for",
        playhead: "Current frame playhead",
        seekAnimation: "Drag playhead to seek animation",
        stepRange: "Step range",
      };
  }
}

export function TimelineStepEditor({
  animationDuration,
  fps,
  currentFrame,
  currentTimeSeconds,
  steps,
  isPlaying = true,
  language = "en",
  onSeek,
  onPlayPause,
  onStepSelect,
  onMarkerChange,
  onStepsChange,
  className,
  disabled = false,
}: TimelineEditorProps) {
  const copy = getTimelineCopy(language);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selectedBoundary, setSelectedBoundary] = useState<StepBoundary>("end");
  const [nameFocusToken, setNameFocusToken] = useState(0);
  const [saveConfirmation, setSaveConfirmation] = useState<string | null>(null);

  const totalFrames = useMemo(
    () => getTotalFrames(animationDuration, fps),
    [animationDuration, fps],
  );
  const defaultStepLengthFrames = useMemo(() => secondsToFrames(2, fps), [fps]);

  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => a.order - b.order),
    [steps],
  );

  const activeStep = useMemo(
    () =>
      sortedSteps.find(
        (step) =>
          currentFrame >= step.startFrame && currentFrame <= step.endFrame,
      ) ?? null,
    [currentFrame, sortedSteps],
  );

  const selectedStep = useMemo(
    () => sortedSteps.find((step) => step.id === selectedStepId) ?? null,
    [sortedSteps, selectedStepId],
  );

  useEffect(() => {
    if (
      selectedStepId &&
      !sortedSteps.some((step) => step.id === selectedStepId)
    ) {
      setSelectedStepId(null);
      onStepSelect?.(null);
    }
  }, [onStepSelect, selectedStepId, sortedSteps]);

  useEffect(() => {
    if (!selectedStep) return;
    if (currentFrame > selectedStep.endFrame) {
      setSelectedStepId(null);
      onStepSelect?.(null);
    }
  }, [currentFrame, onStepSelect, selectedStep]);

  useEffect(() => {
    if (!saveConfirmation) return;
    const timeout = window.setTimeout(() => setSaveConfirmation(null), 1600);
    return () => window.clearTimeout(timeout);
  }, [saveConfirmation]);

  const emitStepsChange = useCallback(
    (nextSteps: TimelineStep[]) => {
      onStepsChange?.(nextSteps);
    },
    [onStepsChange],
  );

  const selectStep = useCallback(
    (stepId: string | null) => {
      setSelectedStepId(stepId);
      const step = stepId
        ? (sortedSteps.find((candidate) => candidate.id === stepId) ?? null)
        : null;
      onStepSelect?.(step);
    },
    [onStepSelect, sortedSteps],
  );

  const handleCreateStep = useCallback(
    (frame: number) => {
      if (disabled) return;

      const nextOrder =
        sortedSteps.length > 0
          ? Math.max(...sortedSteps.map((step) => step.order)) + 1
          : 1;
      const color =
        STEP_COLOR_PALETTE[(nextOrder - 1) % STEP_COLOR_PALETTE.length];
      const startFrame = Math.max(0, Math.min(frame, totalFrames - 1));

      const createdStep = sanitizeStep(
        {
          id: `step_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
          order: nextOrder,
          name: `${copy.stepLabel} ${String(nextOrder).padStart(2, "0")}`,
          stepText: "",
          startFrame,
          endFrame: Math.min(totalFrames, startFrame + defaultStepLengthFrames),
          color,
        },
        totalFrames,
      );

      const nextSteps = [...sortedSteps, createdStep].sort(
        (a, b) => a.order - b.order,
      );
      emitStepsChange(nextSteps);
      selectStep(createdStep.id);
      setNameFocusToken((value) => value + 1);
    },
    [
      copy.stepLabel,
      defaultStepLengthFrames,
      disabled,
      emitStepsChange,
      selectStep,
      sortedSteps,
      totalFrames,
    ],
  );

  const updateSingleStep = useCallback(
    (updatedStep: TimelineStep, emitMarkerChange: boolean) => {
      const sanitized = sanitizeStep(updatedStep, totalFrames);
      const nextSteps = sortedSteps.map((step) =>
        step.id === sanitized.id ? sanitized : step,
      );
      emitStepsChange(nextSteps);
      if (emitMarkerChange) {
        onMarkerChange?.(sanitized);
      }
    },
    [emitStepsChange, onMarkerChange, sortedSteps, totalFrames],
  );

  const handleDeleteStep = useCallback(
    (stepId: string) => {
      if (disabled) return;
      const nextSteps = sortedSteps.filter((step) => step.id !== stepId);
      emitStepsChange(nextSteps);
      if (selectedStepId === stepId) {
        setSelectedStepId(null);
        onStepSelect?.(null);
      }
    },
    [disabled, emitStepsChange, onStepSelect, selectedStepId, sortedSteps],
  );

  const handleDragBoundary = useCallback(
    (stepId: string, boundary: StepBoundary, frame: number) => {
      if (disabled) return;
      const step = sortedSteps.find((item) => item.id === stepId);
      if (!step) return;

      const updated = updateStepBoundary(step, boundary, frame, totalFrames);
      updateSingleStep(updated, true);
    },
    [disabled, sortedSteps, totalFrames, updateSingleStep],
  );

  const handleDragEnd = useCallback(
    (stepId: string) => {
      const step = sortedSteps.find((item) => item.id === stepId);
      if (!step) return;
      onMarkerChange?.(step);
    },
    [onMarkerChange, sortedSteps],
  );

  const nudgeSelectedBoundary = useCallback(
    (delta: number) => {
      if (!selectedStep || disabled) return;
      const updated = updateStepBoundary(
        selectedStep,
        selectedBoundary,
        selectedBoundary === "start"
          ? selectedStep.startFrame + delta
          : selectedStep.endFrame + delta,
        totalFrames,
      );
      updateSingleStep(updated, true);
    },
    [disabled, selectedBoundary, selectedStep, totalFrames, updateSingleStep],
  );

  const addStepAtPlayhead = useCallback(() => {
    handleCreateStep(currentFrame);
  }, [currentFrame, handleCreateStep]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      const targetElement = event.target as HTMLElement | null;
      const isTypingContext =
        !!targetElement &&
        (targetElement.tagName === "INPUT" ||
          targetElement.tagName === "TEXTAREA" ||
          targetElement.tagName === "SELECT" ||
          targetElement.isContentEditable);

      if (isTypingContext) {
        return;
      }

      if (event.key.toLowerCase() === "a") {
        event.preventDefault();
        addStepAtPlayhead();
        return;
      }

      if (
        (event.key === "Delete" || event.key === "Backspace") &&
        selectedStepId
      ) {
        event.preventDefault();
        handleDeleteStep(selectedStepId);
        return;
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        if (!selectedStepId) return;
        event.preventDefault();
        const multiplier = event.shiftKey ? 10 : 1;
        const direction = event.key === "ArrowLeft" ? -1 : 1;
        nudgeSelectedBoundary(direction * multiplier);
      }
    },
    [
      addStepAtPlayhead,
      disabled,
      handleDeleteStep,
      nudgeSelectedBoundary,
      selectedStepId,
    ],
  );

  return (
    <section
      className={`timeline-editor${className ? ` ${className}` : ""}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 18,
        padding: 18,
        background: "rgba(30, 30, 35, 0.8)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 24px 40px rgba(0,0,0,0.28)",
        color: "rgba(255,255,255,0.9)",
        outline: "none",
        display: "grid",
        gap: 16,
      }}
      data-oid="ywocvkj"
    >
      <TimelineRuler
        totalFrames={totalFrames}
        fps={fps}
        animationDuration={animationDuration}
        data-oid="1rrxise"
      />

      <TimelineTrack
        totalFrames={totalFrames}
        currentFrame={currentFrame}
        steps={sortedSteps}
        selectedStepId={selectedStepId}
        activeStepId={activeStep?.id ?? null}
        stepRangeLabel={copy.stepRange}
        startHandleLabel={copy.startHandle}
        endHandleLabel={copy.endHandle}
        handleForLabel={copy.handleFor}
        playheadLabel={copy.playhead}
        seekLabel={copy.seekAnimation}
        disabled={disabled}
        onSeek={onSeek}
        onCreateStep={handleCreateStep}
        onSelectStep={selectStep}
        onDragBoundary={handleDragBoundary}
        onDragEnd={handleDragEnd}
        onBoundaryFocus={(boundary) => setSelectedBoundary(boundary)}
        data-oid="iwxu8hn"
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: "rgba(255,255,255,0.7)",
          gap: 12,
        }}
        data-oid="gtxo26z"
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: 8 }}
          data-oid="xki-jik"
        >
          <button
            type="button"
            onClick={onPlayPause}
            disabled={disabled || !onPlayPause}
            aria-label={isPlaying ? copy.pausePlayback : copy.playPlayback}
            style={{
              width: 36,
              height: 36,
              borderRadius: 9999,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(20, 25, 36, 0.82)",
              color: "rgba(255,255,255,0.95)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
              cursor: disabled || !onPlayPause ? "not-allowed" : "pointer",
            }}
            data-oid="h32davw"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18 }}
              data-oid="hp8bttm"
            >
              {isPlaying ? "pause" : "play_arrow"}
            </span>
          </button>
          <span data-oid="x13nttt">
            {isPlaying ? copy.playing : copy.paused}
          </span>
        </div>
        <span style={{ fontFamily: MONOSPACE_FONT }} data-oid="65svm_9">
          {copy.currentFrame}: {currentFrame}
        </span>
        <span style={{ fontFamily: MONOSPACE_FONT }} data-oid="vqqmcvv">
          {copy.totalFrames}: {totalFrames}
        </span>
        <span style={{ fontFamily: MONOSPACE_FONT }} data-oid="b-tyti-">
          {copy.currentTime}:{" "}
          {currentTimeSeconds !== undefined
            ? `${currentTimeSeconds.toFixed(2)}s`
            : frameToTimeLabel(currentFrame, fps)}
        </span>
      </div>

      {saveConfirmation && (
        <div
          role="status"
          aria-live="polite"
          style={{
            justifySelf: "start",
            fontSize: 12,
            color: "rgba(187, 247, 208, 0.95)",
            border: "1px solid rgba(34,197,94,0.45)",
            background: "rgba(22,163,74,0.2)",
            borderRadius: 8,
            padding: "6px 10px",
          }}
          data-oid="mwdaq4t"
        >
          {saveConfirmation}
        </div>
      )}

      <StepInspector
        selectedStep={selectedStep}
        totalFrames={totalFrames}
        autoFocusToken={nameFocusToken}
        disabled={disabled}
        emptyStateLabel={copy.selectStep}
        stepNameLabel={copy.stepName}
        stepTextLabel={copy.stepText}
        stepTextPlaceholder={copy.stepTextPlaceholder}
        startFrameLabel={copy.startFrame}
        endFrameLabel={copy.endFrame}
        saveStepLabel={copy.saveStep}
        deleteStepLabel={copy.deleteStep}
        untitledStepLabel={copy.untitledStep}
        idLabel={copy.id}
        onSave={(step) => {
          updateSingleStep(step, false);
          setSaveConfirmation(`${copy.saved} ${step.name}`);
          setSelectedStepId(null);
          onStepSelect?.(null);
        }}
        onDelete={handleDeleteStep}
        data-oid="944o_8i"
      />
    </section>
  );
}
